// The church drawer: what the register knows about one church, when it was
// last checked, where it came from, and the doors out (parish page,
// directions, website, the churches next to it).

import { markSvg } from './marks.js';
import { esc } from './panel.js';
import { nextMass, describeNext, weekReadout, openNow, nextWindow, formatMinutes } from './schedule.js';
import { haversineMiles, formatMiles } from './geo.js';
import { LANGUAGE_NAMES } from './i18n.js';

export function createDrawer(ctx) {
  const { t, lang, data, store } = ctx;
  const root = document.getElementById('atlasDrawer');
  const body = root.querySelector('.atlas-drawer-body');
  const langIndex = data.tenant.languageIndex || null;
  const langName = (code) => t('lang.' + code, LANGUAGE_NAMES[code] || code);
  const dayNames = [0, 1, 2, 3, 4, 5, 6].map((d) => t('day.' + d));
  let current = null;
  let detailCache = new Map();

  root.addEventListener('click', (e) => {
    if (e.target.closest('[data-close]')) { ctx.onClose(); return; }
    const b = e.target.closest('[data-id]'); if (b) { ctx.onSelect(b.dataset.id, 'drawer'); return; }
    if (e.target.closest('[data-share]')) share(e.target.closest('[data-share]'));
  });

  function share(btn) {
    const url = location.href;
    const done = () => { const old = btn.textContent; btn.textContent = t('drawer.copied'); setTimeout(() => { btn.textContent = old; }, 1600); };
    if (navigator.clipboard) navigator.clipboard.writeText(url).then(done, () => prompt(t('drawer.share'), url));
    else prompt(t('drawer.share'), url);
  }

  function windowsHtml(rows, label, nowLabel) {
    if (!rows || !rows.length) return '';
    const clock = ctx.clock();
    const now = openNow(rows, clock);
    const byDay = new Map();
    for (const r of rows) { if (!byDay.has(r[0])) byDay.set(r[0], []); byDay.get(r[0]).push(`${formatMinutes(r[1], lang)}–${formatMinutes(r[2], lang)}`); }
    const order = [0, 6, 1, 2, 3, 4, 5].filter((d) => byDay.has(d));
    return `<section class="atlas-dsec"><h3>${esc(label)}${now ? ` <i class="atlas-now">${esc(nowLabel)}</i>` : ''}</h3>
      <dl class="atlas-week">${order.map((d) => `<div><dt>${esc(dayNames[d].slice(0, 3))}</dt><dd>${esc(byDay.get(d).join(', '))}</dd></div>`).join('')}</dl></section>`;
  }

  function render(c, details) {
    current = c;
    const clock = ctx.clock(c);
    const sched = !!data.tenant.schedules;
    const next = sched ? nextMass(c.ms, clock, langIndex) : null;
    const week = sched ? weekReadout(c.ms, langIndex, lang, dayNames) : [];
    const langs = [...new Set([...(c.l || []), ...(c.ms || []).map((r) => langIndex ? langIndex[r[2]] : null)])].filter(Boolean);
    const d = details || {};
    const massText = !sched && d.liturgy && d.liturgy.mass_times ? d.liturgy.mass_times : (!sched && d.church && d.church.mass_times) || '';
    const photo = c.ph || (d.photo && d.photo.url) || (d.church && d.church.hero_image_url) || '';
    const region = [c.r, c.d ? t('drawer.deanery', { n: c.d }) : ''].filter(Boolean).join(' · ');
    const sources = [];
    for (const url of [c.su, d.liturgy && d.liturgy.source_url, c.w]) {
      if (!url) continue;
      const label = hostOf(url);
      if (!sources.some((x) => x.label === label)) sources.push({ url, label });
    }
    const verified = c.v || (d.data_quality && d.data_quality.last_verified ? String(d.data_quality.last_verified).slice(0, 10) : '');
    const parishUrl = c.s ? (lang === 'es' ? '/es' : '') + '/church/' + encodeURIComponent(c.s) : '';
    const dirUrl = `https://www.google.com/maps/dir/?api=1&destination=${c.lat},${c.lng}`;
    const nearby = data.churches.filter((x) => x.i !== c.i).map((x) => ({ x, mi: haversineMiles(c.lat, c.lng, x.lat, x.lng) })).sort((a, b) => a.mi - b.mi).slice(0, 5);
    const similar = data.churches.filter((x) => x.i !== c.i && sameProfile(c, x)).map((x) => ({ x, mi: haversineMiles(c.lat, c.lng, x.lat, x.lng) })).sort((a, b) => a.mi - b.mi).slice(0, 3);

    body.innerHTML = `
      <header class="atlas-dhead">
        <p class="atlas-dkind">${markSvg(c.k, 14)} ${esc(t('kind.' + c.k))}${c.e ? ` · ${esc(t('drawer.founded', { n: c.e }))}` : ''}</p>
        <h2>${esc(c.n)}</h2>
        <p class="atlas-daddr">${esc([c.a, c.c, c.st].filter(Boolean).join(', '))}</p>
        ${region ? `<p class="atlas-dregion">${esc(region)}</p>` : ''}
      </header>
      ${photo ? `<figure class="atlas-dphoto"><img src="${esc(photo)}" alt="" loading="lazy" decoding="async"></figure>` : ''}
      <div class="atlas-dactions">
        ${parishUrl ? `<a class="atlas-btn atlas-btn-primary" href="${esc(parishUrl)}">${esc(t('drawer.open'))}</a>` : ''}
        <a class="atlas-btn" href="${esc(dirUrl)}" target="_blank" rel="noopener">${esc(t('drawer.directions'))}</a>
        ${c.w ? `<a class="atlas-btn" href="${esc(withScheme(c.w))}" target="_blank" rel="noopener">${esc(t('drawer.website'))}</a>` : ''}
        <button type="button" class="atlas-btn" data-share>${esc(t('drawer.share'))}</button>
      </div>
      ${sched ? (next ? `<section class="atlas-dsec atlas-dnext"><h3>${esc(t('drawer.next'))}</h3><p><b>${esc(describeNext(next, lang, dayNames))}</b>${next.lang ? ` · ${esc(langName(next.lang))}` : ''}</p></section>` : `<section class="atlas-dsec"><p class="atlas-muted">${esc(t('drawer.noSchedule'))}</p></section>`) : ''}
      ${week.length ? `<section class="atlas-dsec"><h3>${esc(t('drawer.masses'))}</h3><dl class="atlas-week">${week.map((w) => `<div><dt>${esc(w.name.slice(0, 3))}</dt><dd>${w.times.map((x) => `<span>${esc(x.label)}${x.code && x.code !== 'eng' ? `<small>${esc(langName(x.code))}</small>` : ''}</span>`).join('')}</dd></div>`).join('')}</dl></section>` : ''}
      ${massText ? `<section class="atlas-dsec"><h3>${esc(t('drawer.masses'))}</h3><p class="atlas-dtext">${esc(massText)}</p></section>` : ''}
      ${windowsHtml(c.cs, t('drawer.confession'), t('drawer.confessionNow'))}
      ${c.hc && !(c.cs && c.cs.length) ? `<section class="atlas-dsec"><h3>${esc(t('drawer.confession'))}</h3><p class="atlas-muted">${esc(t('drawer.byAppointment'))}</p></section>` : ''}
      ${windowsHtml(c.as, t('drawer.adoration') + (c.hp ? ' · ' + t('drawer.perpetual') : ''), t('drawer.adorationNow'))}
      ${c.ha && !(c.as && c.as.length) ? `<section class="atlas-dsec"><h3>${esc(t('drawer.adoration'))}</h3><p class="atlas-muted">${esc(c.hp ? t('drawer.perpetual') : t('drawer.byAppointment'))}</p></section>` : ''}
      ${langs.length || c.sc || c.lm ? `<section class="atlas-dsec atlas-dfacts">${langs.length ? `<p><b>${esc(t('drawer.languages'))}</b> ${esc(langs.map(langName).join(', '))}</p>` : ''}${c.lm ? `<p>${esc(t('facet.lm'))}</p>` : ''}${c.sc ? `<p>${esc(c.sc > 1 ? t('drawer.schools', { n: c.sc }) : t('drawer.school'))}</p>` : ''}</section>` : ''}
      <section class="atlas-dsec atlas-dprov">
        ${c.su ? `<p class="atlas-muted">${lang === 'es' ? 'Confirma los horarios con la parroquia antes de salir.' : 'Confirm Mass times with the parish before travelling.'}</p>` : ''}
        <p class="${verified ? '' : 'atlas-muted'}">${esc(verified ? t('drawer.verified', { d: verified }) : t('drawer.unverified'))}</p>
        ${sources.length ? `<p><b>${esc(t('drawer.source'))}</b> ${sources.map((s) => `<a href="${esc(withScheme(s.url))}" target="_blank" rel="noopener">${esc(s.label)}</a>`).join(', ')}</p>` : ''}
        <p><a href="/contact?topic=correction&parish=${encodeURIComponent(c.n)}">${esc(t('drawer.report'))}</a></p>
      </section>
      ${nearby.length ? `<section class="atlas-dsec"><h3>${esc(t('drawer.nearby'))}</h3><ul class="atlas-dlist">${nearby.map(({ x, mi }) => `<li><button type="button" class="atlas-link" data-id="${esc(x.i)}">${markSvg(x.k, 12)} <span>${esc(x.n)}</span><small>${esc(formatMiles(mi, lang))}</small></button></li>`).join('')}</ul></section>` : ''}
      ${similar.length ? `<section class="atlas-dsec"><h3>${esc(t('drawer.similar'))}</h3><ul class="atlas-dlist">${similar.map(({ x, mi }) => `<li><button type="button" class="atlas-link" data-id="${esc(x.i)}">${markSvg(x.k, 12)} <span>${esc(x.n)}</span><small>${esc([x.c, formatMiles(mi, lang)].filter(Boolean).join(' · '))}</small></button></li>`).join('')}</ul></section>` : ''}`;
    root.hidden = false;
    root.scrollTop = 0;
    document.body.classList.add('atlas-drawer-open');
  }

  function sameProfile(a, b) {
    const la = new Set(a.l || []), lb = new Set(b.l || []);
    const shared = [...la].some((x) => x !== 'eng' && lb.has(x)) || (la.size <= 1 && lb.size <= 1);
    return shared && !!a.hc === !!b.hc && !!a.ha === !!b.ha && a.k === b.k;
  }

  async function open(c) {
    render(c, detailCache.get(c.i));
    if (!c.s || detailCache.has(c.i)) return;
    try {
      const r = await fetch('/api/churches/' + encodeURIComponent(c.s));
      if (!r.ok) return;
      const d = await r.json();
      detailCache.set(c.i, d);
      if (current && current.i === c.i) render(c, d);
    } catch (_) { /* the register already gave us enough */ }
  }

  function close() {
    root.hidden = true; current = null;
    document.body.classList.remove('atlas-drawer-open');
  }

  return { open, close, isOpen: () => !root.hidden, current: () => current };
}

function hostOf(url) { try { return new URL(withScheme(url)).hostname.replace(/^www\./, ''); } catch (_) { return url; } }
function withScheme(url) { return /^https?:\/\//i.test(url) ? url : 'https://' + url; }
